import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const L = ({ children }) => <Label className="text-[10px] tracking-widest uppercase opacity-70">{children}</Label>;
const Foot = ({ onClose, onOk, busy, label, testId }) => (
  <div className="flex justify-end gap-2 pt-1">
    <button type="button" onClick={onClose} className="px-4 py-2 rounded border border-border text-[10px] tracking-widest uppercase">Cancel</button>
    <button type="button" onClick={onOk} disabled={busy} data-testid={testId} className="btn-volt px-5 py-2 rounded text-[10px] tracking-widest uppercase disabled:opacity-40">{busy ? "Working…" : label}</button>
  </div>
);
const clashText = (d) => typeof d === "object" && d ? (d.message || d.detail || `Slot already booked${d.team_name ? ` by ${d.team_name}` : ""}`) : d;

export function AcceptHomeDialog({ req, onClose, onDone }) {
  const [pitches, setPitches] = useState([]);
  const [f, setF] = useState({ pitch_id: "", booking_date: "", start: "10:00", duration: 90, note_to_opposition: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!req) return;
    setF({ pitch_id: "", booking_date: req.preferred_date, start: req.kick_off, duration: 90, note_to_opposition: "" });
    api.get("/pitches").then(r => { const ps = (r.data || []).filter(p => p.active !== false); setPitches(ps); if (ps[0]) setF(x => ({ ...x, pitch_id: ps[0].id })); }).catch(() => {});
  }, [req]);
  const ok = async () => {
    if (!f.pitch_id) return toast.error("Pick a pitch");
    const [sh, sm] = f.start.split(":").map(Number); const endMin = sh * 60 + sm + Number(f.duration);
    setBusy(true);
    try {
      const { data } = await api.post(`/friendlies/${req.id}/accept-home`, { pitch_id: f.pitch_id, booking_date: f.booking_date, start_hour: sh, start_min: sm, end_hour: Math.floor(endMin / 60), end_min: endMin % 60 - (endMin % 60) % 15, note_to_opposition: f.note_to_opposition || null });
      toast.success("Booked — opposition coach emailed"); onDone(data);
    } catch (err) { toast.error(clashText(err.response?.data?.detail) || formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!req} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="friendly-home-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Accept — we host</DialogTitle></DialogHeader>
        <p className="text-xs opacity-60">Creates a friendly game booking in the calendar for {req?.team_name} v {req?.opposition_club}.</p>
        <div className="space-y-3">
          <div><L>Pitch</L><Select value={f.pitch_id} onValueChange={v => setF({ ...f, pitch_id: v })}><SelectTrigger data-testid="friendly-home-pitch"><SelectValue placeholder="Pick a pitch" /></SelectTrigger>
            <SelectContent>{pitches.map(p => <SelectItem key={p.id} value={p.id}>{p.venue_short_code ? `${p.venue_short_code} · ` : ""}{p.name}{p.game_format ? ` (${p.game_format})` : ""}</SelectItem>)}</SelectContent></Select></div>
          <div className="grid grid-cols-3 gap-2">
            <div><L>Date</L><Input type="date" value={f.booking_date} onChange={e => setF({ ...f, booking_date: e.target.value })} data-testid="friendly-home-date" /></div>
            <div><L>Kick-off</L><Input type="time" step={900} value={f.start} onChange={e => setF({ ...f, start: e.target.value })} data-testid="friendly-home-time" /></div>
            <div><L>Minutes</L><Select value={String(f.duration)} onValueChange={v => setF({ ...f, duration: Number(v) })}><SelectTrigger data-testid="friendly-home-duration"><SelectValue /></SelectTrigger><SelectContent>{[60, 75, 90, 105, 120].map(m => <SelectItem key={m} value={String(m)}>{m}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div><L>Note to opposition (optional)</L><Textarea rows={2} value={f.note_to_opposition} onChange={e => setF({ ...f, note_to_opposition: e.target.value })} placeholder="Parking at the rear, changing rooms open 30 mins before…" data-testid="friendly-home-note" /></div>
          <Foot onClose={onClose} onOk={ok} busy={busy} label="Book & accept" testId="friendly-home-confirm" />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AcceptAwayDialog({ req, onClose, onDone }) {
  const [f, setF] = useState({ booking_date: "", kick_off: "10:00", their_ground: "", note_to_opposition: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (req) setF({ booking_date: req.preferred_date, kick_off: req.kick_off, their_ground: req.their_ground || "", note_to_opposition: "" }); }, [req]);
  const ok = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/friendlies/${req.id}/accept-away`, { ...f, their_ground: f.their_ground || null, note_to_opposition: f.note_to_opposition || null }); toast.success("Away fixture added — opposition coach emailed"); onDone(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!req} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="friendly-away-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Accept — away</DialogTitle></DialogHeader>
        <p className="text-xs opacity-60">Adds an away friendly to Fixtures for {req?.team_name} at {req?.opposition_club}.</p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><L>Date</L><Input type="date" value={f.booking_date} onChange={e => setF({ ...f, booking_date: e.target.value })} data-testid="friendly-away-date" /></div>
            <div><L>Kick-off</L><Input type="time" step={900} value={f.kick_off} onChange={e => setF({ ...f, kick_off: e.target.value })} data-testid="friendly-away-time" /></div>
          </div>
          <div><L>Their ground / address</L><Input value={f.their_ground} onChange={e => setF({ ...f, their_ground: e.target.value })} placeholder="Ground name, postcode" data-testid="friendly-away-ground" /></div>
          <div><L>Note to opposition (optional)</L><Textarea rows={2} value={f.note_to_opposition} onChange={e => setF({ ...f, note_to_opposition: e.target.value })} data-testid="friendly-away-note" /></div>
          <Foot onClose={onClose} onOk={ok} busy={busy} label="Accept away" testId="friendly-away-confirm" />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CounterDialog({ req, onClose, onDone }) {
  const [f, setF] = useState({ booking_date: "", kick_off: "10:00", venue_preference: "either", message: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (req) setF({ booking_date: req.preferred_date, kick_off: req.kick_off, venue_preference: req.venue_preference || "either", message: "" }); }, [req]);
  const ok = async () => {
    if (f.booking_date === req.preferred_date && f.kick_off === req.kick_off) return toast.error("Change the date or time to make a counter-offer");
    setBusy(true);
    try { const { data } = await api.post(`/friendlies/${req.id}/counter`, { ...f, message: f.message || null }); toast.success("Counter-offer emailed — they can accept or decline with one tap"); onDone(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!req} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="friendly-counter-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Propose another date</DialogTitle></DialogHeader>
        <p className="text-xs opacity-60">They asked for {req?.preferred_date} at {req?.kick_off}. The opposition coach gets an email with Yes / No buttons; if they say yes the request comes back for you to confirm home or away.</p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><L>Date</L><Input type="date" value={f.booking_date} onChange={e => setF({ ...f, booking_date: e.target.value })} data-testid="friendly-counter-date" /></div>
            <div><L>Kick-off</L><Input type="time" step={900} value={f.kick_off} onChange={e => setF({ ...f, kick_off: e.target.value })} data-testid="friendly-counter-time" /></div>
          </div>
          <div><L>Venue</L><Select value={f.venue_preference} onValueChange={v => setF({ ...f, venue_preference: v })}><SelectTrigger data-testid="friendly-counter-venue"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="we_host">We host</SelectItem><SelectItem value="they_host">They host</SelectItem><SelectItem value="either">Either</SelectItem></SelectContent></Select></div>
          <div><L>Message (optional)</L><Textarea rows={2} value={f.message} onChange={e => setF({ ...f, message: e.target.value })} placeholder="We're away that weekend — how about the week after?" data-testid="friendly-counter-message" /></div>
          <Foot onClose={onClose} onOk={ok} busy={busy} label="Send counter-offer" testId="friendly-counter-confirm" />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DeclineDialog({ req, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (req) setReason(""); }, [req]);
  const ok = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/friendlies/${req.id}/decline`, { reason: reason || null }); toast.success("Declined — opposition coach emailed"); onDone(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!req} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="friendly-decline-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Decline request</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><L>Reason (optional, included in the email)</L><Textarea rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="We already have a fixture that weekend…" data-testid="friendly-decline-reason" /></div>
          <Foot onClose={onClose} onOk={ok} busy={busy} label="Decline" testId="friendly-decline-confirm" />
        </div>
      </DialogContent>
    </Dialog>
  );
}
